import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, Grid } from '@mui/material';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import { VIDEOS, VideoItem, VideoType } from './source';
import ScrollableVideos from './videosList';

interface Props {
    selectedOption: VideoType;
}

const VideosSection: React.FC<Props> = ({ selectedOption }) => {
    const { ref: inViewRef, inView } = useInView({ threshold: 0.5 });
    const [activeVideo, setActiveVideo] = React.useState<VideoItem | null>(null);
    const [play, setPlay] = React.useState(false);
    const videoRef = React.useRef<HTMLVideoElement | null>(null);
    const [showVideo, setShowVideo] = React.useState(false);
    const videoContainerRef = React.useRef<HTMLDivElement | null>(null);
    const [videoHeight, setVideoHeight] = React.useState<number>(0);

    const videos = VIDEOS[selectedOption];

    React.useEffect(() => {
        if (videos.length > 0) {
            setActiveVideo(videos[0]);
            setShowVideo(false);
            setPlay(false);
        }
    }, [selectedOption]);

    const playVideo = () => {
        videoRef.current?.play();
        setShowVideo(true);
        setPlay(true);
    };

    const pauseVideo = () => {
        videoRef.current?.pause();
        setShowVideo(false);
        setPlay(false);
    };

    const toggleVideo = () => {
        if (!showVideo) playVideo();
        else play ? pauseVideo() : playVideo();
    };

    const handleSelectVideo = (video: VideoItem) => {
        setActiveVideo(video);
        toggleVideo();
    };

    React.useEffect(() => {
        if (!inView && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
            setPlay(false);
            setShowVideo(false);
        }
    }, [inView]);

    React.useEffect(() => {
        const updateHeight = () => {
            if (videoContainerRef.current) {
                setVideoHeight(videoContainerRef.current.offsetHeight);
            }
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [activeVideo]);

    return (
        <Grid container gap={2} justifyContent="center" flexWrap="wrap">
            <Grid item xs={12} md={7} lg={7} sx={{ position: 'relative', minWidth: 300, maxWidth: 900, height: '100%' }}>
                <Box
                    ref={(el) => {
                        inViewRef(el as HTMLDivElement | null);
                        videoContainerRef.current = el as HTMLDivElement | null;
                    }}
                    sx={{
                        position: 'relative',
                        borderRadius: '16px',
                        width: '100%',
                        boxShadow: '0 0 12px rgba(0, 0, 0, 0.3)',
                        paddingTop: '56.25%',
                        overflow: 'hidden',
                    }}
                >
                    {activeVideo && (showVideo ? (
                        <video
                            ref={videoRef}
                            src={activeVideo.video}
                            controls
                            muted
                            loop
                            autoPlay
                            playsInline
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                borderRadius: '16px',
                            }}
                        />
                    ) : (
                        <img
                            src={activeVideo.thumbnail}
                            alt={activeVideo.title}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '16px',
                                boxShadow: '0 0 12px rgba(0, 0, 0, 0.3)',
                            }}
                        />
                    ))}

                    {activeVideo && (
                        <Box
                            component="button"
                            onClick={(e) => { e.stopPropagation(); toggleVideo(); }}
                            sx={{
                                position: 'absolute',
                                top: {
                                    lg: '48%',
                                    md: '47%',
                                    xs: '45%'
                                },
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#fff',
                                backgroundColor: '#000',
                                border: '3px solid rgba(221, 218, 218, 0.3)',
                                boxShadow: '0 0 8px rgba(0, 0, 0, 0.6)',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                width: '65px',
                                height: '65px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontSize: '32px',
                                WebkitTapHighlightColor: 'transparent',
                                outline: 'none',
                            }}
                        >
                            {play ? <PauseIcon /> : <PlayArrowIcon />}
                        </Box>
                    )}
                </Box>
            </Grid>

            <Grid item xs={12} md={2.5} lg={2.5} height='100%'>
                <ScrollableVideos
                    videos={videos}
                    activeVideoId={activeVideo?.id || null}
                    playingVideoId={play && activeVideo ? activeVideo.id : null}
                    onSelect={handleSelectVideo}
                    height={videoHeight}
                />
            </Grid>
        </Grid>
    );
};

export default VideosSection;
